/**
 * Query Detail Page
 * View individual flip request with calculation and approval options
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Home,
  Calendar,
  DollarSign,
  TrendingUp,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import API_CONSTRUCTOR from '../../models/api-model';
import HouseFlippingCalculator from '../../components/calculator';

// Status configuration
const STATUS_CONFIG = {
  pending_initial_calculation: {
    label: 'In Initial Review',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    icon: Clock
  },
  pending_initial_approval: {
    label: 'Ready for Your Review',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    icon: AlertCircle
  },
  pending_inspection_payment: {
    label: 'Awaiting Inspection Payment',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: DollarSign
  },
  pending_agent: {
    label: 'In Review',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: Clock
  },
  pending_owner_approval: {
    label: 'Ready for Your Review',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icon: AlertCircle
  },
  approved_by_owner: {
    label: 'Open to Investors',
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: Users
  },
  investor_committed: {
    label: 'Investors Committed',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    icon: Users
  },
  completed: {
    label: 'Completed',
    color: 'text-green-700',
    bg: 'bg-green-100',
    border: 'border-green-300',
    icon: CheckCircle
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: XCircle
  }
};

const QueryDetail = () => {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const apiModel = API_CONSTRUCTOR();

  const [query, setQuery] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchQueryDetail();
  }, [queryId]);

  const fetchQueryDetail = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch query details
      const queryResponse = await apiModel.get(`/queries/${queryId}`);
      if (!queryResponse.success) {
        throw new Error(queryResponse.error || 'Failed to load query');
      }

      setQuery(queryResponse.data);

      // Fetch calculation if available
      if (['pending_initial_approval', 'pending_inspection_payment', 'pending_owner_approval', 'approved_by_owner', 'investor_committed', 'completed'].includes(queryResponse.data.status)) {
        const calcResponse = await apiModel.get(`/calculations/query/${queryId}`);
        if (calcResponse.success && calcResponse.data) {
          setCalculation(calcResponse.data);
        }
      }
    } catch (err) {
      console.error('Fetch query detail error:', err);
      setError(err.message || 'Failed to load query details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      const response = await apiModel.put(`/queries/${queryId}/approve`, {});

      if (response.success) {
        // Different message based on status
        if (query.status === 'pending_initial_approval') {
          toast.success('Initial calculation accepted! Please proceed with the inspection payment.');
        } else {
          toast.success('Calculation approved! Your property is now open to investors.');
        }
        setShowApproveModal(false);
        fetchQueryDetail(); // Refresh data
      } else {
        throw new Error(response.error || 'Failed to approve');
      }
    } catch (err) {
      console.error('Approve error:', err);
      toast.error(err.message || 'Failed to approve calculation');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsRejecting(true);
      const response = await apiModel.put(`/queries/${queryId}/reject`, {
        reason: rejectReason || 'No reason provided'
      });

      if (response.success) {
        if (query.status === 'pending_initial_approval') {
          toast.success('Thank you for your feedback. Your query has been declined.');
        } else {
          toast.success('Calculation rejected. Our agent will create a revised proposal.');
        }
        setShowRejectModal(false);
        setRejectReason('');
        fetchQueryDetail(); // Refresh data
      } else {
        throw new Error(response.error || 'Failed to reject');
      }
    } catch (err) {
      console.error('Reject error:', err);
      toast.error(err.message || 'Failed to reject calculation');
    } finally {
      setIsRejecting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Skeleton className="h-10 w-1/3 mb-6" />
        <div className="space-y-4">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error || !query) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-600 mb-4">
              <AlertCircle className="h-5 w-5" />
              <p>{error || 'Query not found'}</p>
            </div>
            <Button onClick={() => navigate('/my-queries')} variant="outline">
              Back to My Queries
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[query.status] || STATUS_CONFIG.pending_agent;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/my-queries')}
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to My Queries
      </Button>

      {/* Property Header */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Home className="h-6 w-6 text-primary" />
                <h1 className="text-3xl font-bold">{query.property_address}</h1>
              </div>
              {query.property_city && (
                <p className="text-lg text-muted-foreground">
                  {query.property_city}, {query.property_state} {query.property_zip}
                </p>
              )}
            </div>
            
            <div className="flex flex-col gap-2 items-end">
              {/* Status Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border} border`}>
                <StatusIcon className="h-5 w-5" />
                {statusConfig.label}
              </div>
              
              {/* View Property Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/property/${query.property_id}`)}
                className="gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                View Property
              </Button>
            </div>
          </div>

          {/* Timeline Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Submitted</p>
                <p className="text-sm font-medium">{formatDate(query.created_at)}</p>
              </div>
            </div>
            {query.inspection_fee_paid_date && (
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Inspection Paid</p>
                  <p className="text-sm font-medium">{formatDate(query.inspection_fee_paid_date)}</p>
                </div>
              </div>
            )}
            {query.number_of_investors > 0 && (
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-teal-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Investors</p>
                  <p className="text-sm font-medium">{query.number_of_investors} committed</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Initial Calculation In Progress */}
      {query.status === 'pending_initial_calculation' && (
        <Card className="border-cyan-200 bg-cyan-50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-cyan-900 mb-2">Initial Review in Progress</h3>
                <p className="text-sm text-cyan-800">
                  Our specialist is reviewing your property and preparing an initial calculation. 
                  You'll receive a preliminary analysis shortly to help you decide if you want to proceed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Initial Calculation Ready - Action Required */}
      {query.status === 'pending_initial_approval' && calculation && (
        <Card className="border-sky-200 bg-sky-50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-sky-900 mb-2">Initial Calculation Ready!</h3>
                <p className="text-sm text-sky-800 mb-4">
                  Review this preliminary analysis. If you'd like to proceed, you'll pay the ${query.inspection_fee_amount} inspection fee 
                  and receive a detailed plan with a property visit.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowApproveModal(true)}
                    disabled={isApproving}
                    className="bg-sky-600 hover:bg-sky-700"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Accept & Proceed to Payment
                  </Button>
                  <Button
                    onClick={() => setShowRejectModal(true)}
                    variant="outline"
                    className="border-sky-300 text-sky-700 hover:bg-sky-100"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Not Interested
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inspection Payment Notice */}
      {query.status === 'pending_inspection_payment' && (
        <Card className="border-orange-200 bg-orange-50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-900 mb-2">Action Required: Inspection Payment</h3>
                <p className="text-sm text-orange-800 mb-3">
                  Our specialist will contact you shortly to arrange the ${query.inspection_fee_amount} inspection fee payment. 
                  This fee is fully refundable if you cancel before approving the detailed calculation.
                </p>
                <p className="text-sm text-orange-800">
                  <strong>What's Next:</strong> Once payment is received, our agent will visit your property and create a comprehensive flipping plan.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* In Review Notice */}
      {query.status === 'pending_agent' && (
        <Card className="border-blue-200 bg-blue-50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Detailed Analysis in Progress</h3>
                <p className="text-sm text-blue-800">
                  Our specialist is conducting a thorough analysis of your property and creating a detailed flipping plan. 
                  You'll be notified when the final calculation is ready for your review.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calculation Ready - Action Required */}
      {query.status === 'pending_owner_approval' && calculation && (
        <Card className="border-purple-200 bg-purple-50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900 mb-2">Your Calculation is Ready!</h3>
                <p className="text-sm text-purple-800 mb-4">
                  Our specialist has completed the analysis. Review the details below and approve to open your property to investors.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowApproveModal(true)}
                    disabled={isApproving}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Approve & Open to Investors
                  </Button>
                  <Button
                    onClick={() => setShowRejectModal(true)}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Request Revision
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calculation Details */}
      {calculation && (
        <>
          {/* Full Calculator in Read-Only Mode */}
          <div className="mb-6">
            <HouseFlippingCalculator
              buildingSquareFeet={calculation.home_area}
              estimatedRentAmount={calculation.current_rental}
              estimatedValue={calculation.present_value}
              loanBalance={calculation.loan_amount}
              lotSquareFeet={calculation.lot_size}
              marketValuePerSqft={calculation.market_value_per_sqft}
              pool={calculation.has_pool}
              readOnly={true}
              initialInputs={{
                presentValue: calculation.present_value,
                homeArea: calculation.home_area,
                loanAmount: calculation.loan_amount,
                currentRental: calculation.current_rental,
                lotSize: calculation.lot_size,
                hasPool: calculation.has_pool ? 'yes' : 'no',
                renovationTier: calculation.renovation_tier,
                extraAreaTier: calculation.cost_per_sqft_extra || 400,
                addAreaCheckbox: (calculation.extra_area_added || 0) > 0,
                extraArea: calculation.extra_area_added || 0,
                investorType: calculation.investor_type,
                rentalPeriod: Math.round(calculation.rental_period / 12),
                guaranteedReturnPct: calculation.guaranteed_return_pct || 15,
                upfrontPaymentPct: calculation.upfront_payment_pct || 30,
                paymentParts: calculation.payment_parts || 4,
                marketValuePerSqft: calculation.market_value_per_sqft,
              }}
            />
          </div>

          {/* Agent Notes */}
          {calculation.notes_for_homeowner && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Agent's Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-muted-foreground whitespace-pre-wrap">{calculation.notes_for_homeowner}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Renovation Plan */}
          {calculation.renovation_plan_html && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Renovation Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: calculation.renovation_plan_html }}
                />
                {calculation.renovation_timeline && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900 mb-1">Timeline</p>
                    <p className="text-sm text-blue-800">{calculation.renovation_timeline}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Investor Interest (if approved) */}
      {(query.status === 'approved_by_owner' || query.status === 'investor_committed') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Investor Interest
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{query.number_of_investors || 0}</p>
                  <p className="text-sm text-muted-foreground">Investors Committed</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">{query.total_committed_percentage || 0}%</p>
                  <p className="text-sm text-muted-foreground">Funded</p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-600 h-3 rounded-full transition-all"
                  style={{ width: `${Math.min(query.total_committed_percentage || 0, 100)}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {100 - (query.total_committed_percentage || 0)}% remaining • {formatCurrency(query.remaining_amount_needed)} needed
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approve Confirmation Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {query.status === 'pending_initial_approval' ? 'Accept Initial Calculation' : 'Approve Calculation'}
            </DialogTitle>
            <DialogDescription>
              {query.status === 'pending_initial_approval' 
                ? `Are you sure you want to proceed? You'll need to pay the $${query.inspection_fee_amount} inspection fee for a detailed property analysis.`
                : 'Are you sure you want to approve this calculation? This will open your property to potential investors.'
              }
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              onClick={() => setShowApproveModal(false)}
              variant="outline"
              disabled={isApproving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={isApproving}
              className={query.status === 'pending_initial_approval' ? 'bg-sky-600 hover:bg-sky-700' : 'bg-purple-600 hover:bg-purple-700'}
            >
              {isApproving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {query.status === 'pending_initial_approval' ? 'Accepting...' : 'Approving...'}
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {query.status === 'pending_initial_approval' ? 'Yes, Accept & Proceed' : 'Yes, Approve'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {query.status === 'pending_initial_approval' ? 'Decline Initial Calculation' : 'Request Revision'}
            </DialogTitle>
            <DialogDescription>
              {query.status === 'pending_initial_approval'
                ? 'Let us know why you\'re not interested (optional):'
                : 'Please let us know what you\'d like us to revise:'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejectReason">
                {query.status === 'pending_initial_approval' ? 'Feedback (Optional)' : 'Revision Notes'}
              </Label>
              <Textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                placeholder={query.status === 'pending_initial_approval' 
                  ? 'e.g., Looking for a different type of investment...'
                  : 'e.g., I\'d like a different renovation approach...'
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowRejectModal(false)}
              variant="outline"
              disabled={isRejecting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={isRejecting}
            >
              {isRejecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                query.status === 'pending_initial_approval' ? 'Decline' : 'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QueryDetail;


